import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalBody, ModalHeader, Badge, Form, Input } from "reactstrap";
import classnames from "classnames";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
  Row,
  Col,
  PreviewCard,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  RSelect,
  UserAvatar,
  PaginationComponent,
} from "../../../components/Component";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import { findUpper } from "../../../utils/Utils";
import { Spinner } from "reactstrap";

const CompliancePage = () => {
  const history = useHistory();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("audit-trail");
  const [loading, setLoading] = useState(false);
  
  // Audit Trail
  const [auditTrail, setAuditTrail] = useState([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditItemPerPage] = useState(20);
  const [auditTotalItems, setAuditTotalItems] = useState(0);
  const [auditFilters, setAuditFilters] = useState({
    subject_type: "",
    event: "",
    start_date: "",
    end_date: "",
    user_id: "",
    search: "",
  });
  
  // Traçabilité
  const [traceability, setTraceability] = useState(null);
  const [traceabilitySearch, setTraceabilitySearch] = useState({ type: "Observation", id: "" });
  const [traceabilityEntities, setTraceabilityEntities] = useState([]);
  const [traceabilityEntitiesLoading, setTraceabilityEntitiesLoading] = useState(false);
  const [traceabilityCurrentPage, setTraceabilityCurrentPage] = useState(1);
  const [traceabilityItemPerPage] = useState(20);
  const [traceabilityTotalItems, setTraceabilityTotalItems] = useState(0);
  
  // Rapports d'audit
  const [auditReports, setAuditReports] = useState([]);
  const [reportModal, setReportModal] = useState(false);
  const [newReport, setNewReport] = useState({
    title: "",
    description: "",
    period_start: "",
    period_end: "",
  });
  
  // ISO Compliance
  const [isoRecords, setIsoRecords] = useState([]);
  const [isoStats, setIsoStats] = useState(null);
  const [isoModal, setIsoModal] = useState(false);
  const [editingIso, setEditingIso] = useState(null);
  const [isoForm, setIsoForm] = useState({
    standard: "ISO 45001",
    clause: "",
    requirement: "",
    description: "",
    compliance_status: "not_applicable",
    evidence: "",
    notes: "",
    assessment_date: "",
    next_review_date: "",
  });

  // Déterminer le tab actif en fonction de la route
  useEffect(() => {
    const pathname = location.pathname;
    
    if (pathname.includes("/compliance/audit-trail")) {
      setActiveTab("audit-trail");
    } else if (pathname.includes("/compliance/traceability")) {
      setActiveTab("traceability");
    } else if (pathname.includes("/compliance/audit-reports")) {
      setActiveTab("audit-reports");
    } else if (pathname.includes("/compliance/iso")) {
      setActiveTab("iso");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (activeTab === "audit-reports") {
      loadAuditReports();
    } else if (activeTab === "iso") {
      loadIsoCompliance();
      loadIsoStats();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "traceability") return;
    loadTraceabilityEntities(traceabilitySearch.type);
  }, [activeTab, traceabilitySearch.type]);

  useEffect(() => {
    if (activeTab !== "traceability" || !traceabilitySearch.id || !traceability) return;
    loadTraceability(traceabilityCurrentPage);
  }, [activeTab, traceabilityCurrentPage]);

  useEffect(() => {
    if (activeTab === "audit-trail") {
      loadAuditTrail(auditCurrentPage);
    }
  }, [activeTab, auditCurrentPage]);

  const loadAuditTrail = async (page = auditCurrentPage) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: auditItemPerPage,
      };
      Object.keys(auditFilters).forEach((key) => {
        if (auditFilters[key]) {
          params[key] = auditFilters[key];
        }
      });
      const response = await api.getAuditTrail(params);
      if (Array.isArray(response?.data)) {
        setAuditTrail(response.data);
        setAuditCurrentPage(response.current_page || page);
        setAuditTotalItems(response.total || 0);
      } else if (Array.isArray(response)) {
        setAuditTrail(response);
        setAuditCurrentPage(page);
        setAuditTotalItems(response.length);
      } else {
        setAuditTrail([]);
        setAuditTotalItems(0);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'audit trail:", error);
      toast.error("Erreur lors du chargement de l'audit trail");
    } finally {
      setLoading(false);
    }
  };

  const paginateAuditTrail = (pageNumber) => {
    setAuditCurrentPage(pageNumber);
  };

  const loadTraceability = async (page = traceabilityCurrentPage) => {
    if (!traceabilitySearch.id) {
      toast.warning("Veuillez sélectionner une entité");
      return;
    }
    setLoading(true);
    try {
      const response = await api.getTraceability(traceabilitySearch.type, traceabilitySearch.id, {
        page,
        per_page: traceabilityItemPerPage,
      });
      setTraceability(response);
      setTraceabilityCurrentPage(response?.current_page || page);
      setTraceabilityTotalItems(response?.total_events || 0);
    } catch (error) {
      console.error("Erreur lors du chargement de la traçabilité:", error);
      toast.error("Erreur lors du chargement de la traçabilité");
    } finally {
      setLoading(false);
    }
  };

  const paginateTraceability = (pageNumber) => {
    setTraceabilityCurrentPage(pageNumber);
  };

  const normalizeCollection = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return [];
  };

  const loadTraceabilityEntities = async (type) => {
    setTraceabilityEntitiesLoading(true);
    try {
      let data = [];
      if (type === "Observation") {
        data = normalizeCollection(await api.getObservations({ per_page: 100 }));
      } else if (type === "User") {
        data = normalizeCollection(await api.getUsers({ per_page: 100 }));
      } else if (type === "Site") {
        data = normalizeCollection(await api.getSites());
      }
      setTraceabilityEntities(data);
    } catch (error) {
      setTraceabilityEntities([]);
      toast.error("Impossible de charger la liste des entités");
    } finally {
      setTraceabilityEntitiesLoading(false);
    }
  };

  const traceabilityEntityOptions = traceabilityEntities.map((item) => {
    if (traceabilitySearch.type === "Observation") {
      return {
        value: String(item.id),
        label: `#${item.id} - ${item.title || "Observation sans titre"}`,
      };
    }
    if (traceabilitySearch.type === "User") {
      const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim();
      return {
        value: String(item.id),
        label: `${fullName || item.name || "Utilisateur"} (${item.email || "sans email"})`,
      };
    }
    return {
      value: String(item.id),
      label: `${item.name || "Site"}${item.code ? ` (${item.code})` : ""}`,
    };
  });

  const loadAuditReports = async () => {
    setLoading(true);
    try {
      const response = await api.getAuditReports();
      // Gérer la pagination
      if (response.data) {
        setAuditReports(response.data);
      } else if (Array.isArray(response)) {
        setAuditReports(response);
      } else {
        setAuditReports([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des rapports:", error);
      toast.error("Erreur lors du chargement des rapports");
    } finally {
      setLoading(false);
    }
  };

  const loadIsoCompliance = async () => {
    setLoading(true);
    try {
      const response = await api.getIsoCompliance();
      // Gérer la pagination
      if (response.data) {
        setIsoRecords(response.data);
      } else if (Array.isArray(response)) {
        setIsoRecords(response);
      } else {
        setIsoRecords([]);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la conformité ISO:", error);
      toast.error("Erreur lors du chargement de la conformité ISO");
    } finally {
      setLoading(false);
    }
  };

  const loadIsoStats = async () => {
    try {
      const response = await api.getIsoComplianceStats();
      setIsoStats(response);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques ISO:", error);
    }
  };

  const handleGenerateReport = async () => {
    if (!newReport.title || !newReport.period_start || !newReport.period_end) {
      toast.warning("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      await api.generateAuditTrailReport(newReport);
      toast.success("Rapport généré avec succès");
      setReportModal(false);
      setNewReport({ title: "", description: "", period_start: "", period_end: "" });
      loadAuditReports();
    } catch (error) {
      console.error("Erreur lors de la génération du rapport:", error);
      toast.error("Erreur lors de la génération du rapport");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveIso = async () => {
    if (!isoForm.clause || !isoForm.requirement || !isoForm.description) {
      toast.warning("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      if (editingIso) {
        await api.updateIsoCompliance(editingIso.id, isoForm);
        toast.success("Enregistrement mis à jour avec succès");
      } else {
        await api.createIsoCompliance(isoForm);
        toast.success("Enregistrement créé avec succès");
      }
      setIsoModal(false);
      setEditingIso(null);
      setIsoForm({
        standard: "ISO 45001",
        clause: "",
        requirement: "",
        description: "",
        compliance_status: "not_applicable",
        evidence: "",
        notes: "",
        assessment_date: "",
        next_review_date: "",
      });
      loadIsoCompliance();
      loadIsoStats();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteIso = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet enregistrement ?")) {
      return;
    }
    try {
      await api.deleteIsoCompliance(id);
      toast.success("Enregistrement supprimé avec succès");
      loadIsoCompliance();
      loadIsoStats();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      compliant: { color: "success", label: "Conforme" },
      non_compliant: { color: "danger", label: "Non conforme" },
      partially_compliant: { color: "warning", label: "Partiellement conforme" },
      not_applicable: { color: "secondary", label: "Non applicable" },
    };
    const badge = badges[status] || badges.not_applicable;
    return <Badge color={badge.color}>{badge.label}</Badge>;
  };

  const EVENT_LABELS = {
    created: "Créé",
    updated: "Modifié",
    deleted: "Supprimé",
    restored: "Restauré",
    unknown: "Inconnu",
  };

  const SUBJECT_TYPE_LABELS = {
    User: "Utilisateur",
    Observation: "Observation",
    Site: "Site",
    Company: "Entreprise",
    Role: "Rôle",
    Badge: "Badge",
    Permission: "Permission",
  };

  const getEventLabel = (event) => EVENT_LABELS[event] || event || EVENT_LABELS.unknown;

  const getActionLabel = (description, event) => {
    if (!description) return "-";
    const d = String(description).toLowerCase().trim();
    if (EVENT_LABELS[d]) return EVENT_LABELS[d];
    if (event && EVENT_LABELS[event]) return EVENT_LABELS[event];
    return description;
  };

  const getEventBadge = (event) => {
    const eventColors = {
      created: "success",
      updated: "primary",
      deleted: "danger",
      restored: "warning",
    };
    return <Badge color={eventColors[event] || "secondary"}>{getEventLabel(event)}</Badge>;
  };

  const getSubjectTypeBadge = (type) => {
    if (!type) return <span className="text-soft">-</span>;
    const typeName = type.split("\\").pop();
    const label = SUBJECT_TYPE_LABELS[typeName] || typeName;
    return <Badge color="info">{label}</Badge>;
  };

  const renderAuditTrail = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle page>Audit Trail</BlockTitle>
            <BlockDes className="text-soft">
              <p>Historique complet de toutes les actions et modifications</p>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent>
            <Button color="primary" onClick={() => loadAuditTrail(auditCurrentPage)}>
              <Icon name="refresh" className="me-1" />
              Actualiser
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      <PreviewCard className="card-full mb-4">
        <div className="card-inner">
          <Row className="g-3 align-items-end">
            <Col md="3">
              <div className="form-group">
                <label className="form-label">Type</label>
                <RSelect
                  options={[
                    { value: "", label: "Tous" },
                    { value: "Observation", label: "Observation" },
                    { value: "User", label: "Utilisateur" },
                    { value: "Site", label: "Site" },
                  ]}
                  value={{
                    value: auditFilters.subject_type,
                    label: auditFilters.subject_type || "Tous",
                  }}
                  onChange={(option) =>
                    setAuditFilters({ ...auditFilters, subject_type: option.value })
                  }
                />
              </div>
            </Col>
            <Col md="3">
              <div className="form-group">
                <label className="form-label">Événement</label>
                <RSelect
                  options={[
                    { value: "", label: "Tous" },
                    { value: "created", label: "Créé" },
                    { value: "updated", label: "Modifié" },
                    { value: "deleted", label: "Supprimé" },
                  ]}
                  value={{
                    value: auditFilters.event,
                    label: auditFilters.event || "Tous",
                  }}
                  onChange={(option) =>
                    setAuditFilters({ ...auditFilters, event: option.value })
                  }
                />
              </div>
            </Col>
            <Col md="2">
              <div className="form-group">
                <label className="form-label">Date début</label>
                <Input
                  type="date"
                  value={auditFilters.start_date}
                  onChange={(e) =>
                    setAuditFilters({ ...auditFilters, start_date: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md="2">
              <div className="form-group">
                <label className="form-label">Date fin</label>
                <Input
                  type="date"
                  value={auditFilters.end_date}
                  onChange={(e) =>
                    setAuditFilters({ ...auditFilters, end_date: e.target.value })
                  }
                />
              </div>
            </Col>
            <Col md="2">
              <div className="form-group">
                <label className="form-label">&nbsp;</label>
                <Button
                  color="primary"
                  block
                  onClick={() => {
                    setAuditCurrentPage(1);
                    loadAuditTrail(1);
                  }}
                >
                  Filtrer
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </PreviewCard>

      <Block>
        <DataTable className="card-stretch">
          <DataTableBody>
            <DataTableHead className="nk-tb-item nk-tb-head">
              <DataTableRow>
                <span className="sub-text">Date</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Utilisateur</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Action</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Type</span>
              </DataTableRow>
              <DataTableRow>
                <span className="sub-text">Événement</span>
              </DataTableRow>
              <DataTableRow className="nk-tb-col-tools text-end"></DataTableRow>
            </DataTableHead>
            {loading ? (
              <DataTableItem>
                <DataTableRow>
                  <DataTableItem colSpan={6} className="text-center">
                    <Spinner color="primary" />
                  </DataTableItem>
                </DataTableRow>
              </DataTableItem>
            ) : auditTrail.length === 0 ? (
              <DataTableItem>
                <DataTableRow>
                  <DataTableItem colSpan={6} className="text-center">
                    <PreviewCard>
                      <div className="text-center py-4">
                        <span className="text-soft">Aucun enregistrement d'audit</span>
                      </div>
                    </PreviewCard>
                  </DataTableItem>
                </DataTableRow>
              </DataTableItem>
            ) : (
              auditTrail.map((item) => (
                <DataTableItem key={item.id}>
                  <DataTableRow>
                    <span>{new Date(item.created_at).toLocaleString("fr-FR")}</span>
                  </DataTableRow>
                  <DataTableRow>
                    {item.causer ? (
                      <div className="d-flex align-items-center">
                        <UserAvatar
                          className="me-2"
                          size="sm"
                          theme="blue"
                          text={findUpper(item.causer.name || "S")}
                        />
                        <div>
                          <span className="tb-lead">{item.causer.name || "Système"}</span>
                          {item.causer.email && (
                            <span className="sub-text d-block">{item.causer.email}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <span className="text-soft">Système</span>
                    )}
                  </DataTableRow>
                  <DataTableRow>
                    <span className="tb-lead">{getActionLabel(item.description, item.event)}</span>
                  </DataTableRow>
                  <DataTableRow>
                    {getSubjectTypeBadge(item.subject_type)}
                  </DataTableRow>
                  <DataTableRow>
                    {getEventBadge(item.event)}
                  </DataTableRow>
                  <DataTableRow className="nk-tb-col-tools">
                    <ul className="nk-tb-actions gx-1">
                      <li>
                        <button
                          className="btn btn-trigger btn-icon"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title="Voir les détails"
                        >
                          <Icon name="eye"></Icon>
                        </button>
                      </li>
                    </ul>
                  </DataTableRow>
                </DataTableItem>
              ))
            )}
          </DataTableBody>
          <div className="card-inner">
            {auditTotalItems > 0 && (
              <PaginationComponent
                itemPerPage={auditItemPerPage}
                totalItems={auditTotalItems}
                paginate={paginateAuditTrail}
                currentPage={auditCurrentPage}
              />
            )}
          </div>
        </DataTable>
      </Block>
    </div>
  );

  const renderTraceability = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockHeadContent>
          <BlockTitle page>Traçabilité</BlockTitle>
          <BlockDes className="text-soft">
            <p>Historique complet des modifications d'une entité</p>
          </BlockDes>
        </BlockHeadContent>
      </BlockHead>

      <PreviewCard className="card-full mb-4">
        <div className="card-inner">
          <Row className="g-3 align-items-end">
            <Col md="3">
              <div className="form-group">
                <label className="form-label">Type d'entité</label>
                <RSelect
                  options={[
                    { value: "Observation", label: "Observation" },
                    { value: "User", label: "Utilisateur" },
                    { value: "Site", label: "Site" },
                  ]}
                  value={{
                    value: traceabilitySearch.type,
                    label: traceabilitySearch.type,
                  }}
                  onChange={(option) => {
                    setTraceabilityCurrentPage(1);
                    setTraceabilityTotalItems(0);
                    setTraceability(null);
                    setTraceabilitySearch({ ...traceabilitySearch, type: option.value, id: "" });
                  }}
                />
              </div>
            </Col>
            <Col md="3">
              <div className="form-group">
                <label className="form-label">
                  {traceabilitySearch.type === "Observation"
                    ? "Observation"
                    : traceabilitySearch.type === "User"
                    ? "Utilisateur"
                    : "Site"}
                </label>
                <RSelect
                  options={traceabilityEntityOptions}
                  value={
                    traceabilityEntityOptions.find(
                      (option) => option.value === String(traceabilitySearch.id)
                    ) || null
                  }
                  onChange={(option) => {
                    setTraceabilityCurrentPage(1);
                    setTraceabilityTotalItems(0);
                    setTraceability(null);
                    setTraceabilitySearch({ ...traceabilitySearch, id: option?.value || "" });
                  }}
                  placeholder={
                    traceabilityEntitiesLoading
                      ? "Chargement..."
                      : traceabilitySearch.type === "Observation"
                      ? "Sélectionner une observation"
                      : traceabilitySearch.type === "User"
                      ? "Sélectionner un utilisateur"
                      : "Sélectionner un site"
                  }
                  isSearchable
                  isDisabled={traceabilityEntitiesLoading}
                />
              </div>
            </Col>
            <Col md="2">
              <Button
                color="primary"
                onClick={() => {
                  setTraceabilityCurrentPage(1);
                  setTraceability(null);
                  setTraceabilityTotalItems(0);
                  loadTraceability(1);
                }}
              >
                Rechercher
              </Button>
            </Col>
          </Row>
        </div>
      </PreviewCard>

      {traceability && (
        <PreviewCard className="card-full">
          <div className="card-inner">
            <h6 className="title mb-4">
              Traçabilité : {traceability.subject_type ? (SUBJECT_TYPE_LABELS[traceability.subject_type.split("\\").pop()] || traceability.subject_type) : "-"} #{traceability.subject_id}
            </h6>
            {traceability.timeline && traceability.timeline.length > 0 ? (
              <div className="nk-timeline">
                {traceability.timeline.map((event, index) => (
                  <div key={event.id || index} className="nk-timeline-item">
                    <div className="nk-timeline-time">
                      {new Date(event.created_at).toLocaleString("fr-FR")}
                    </div>
                    <div className="nk-timeline-badge bg-primary">
                      <Icon name="dot" />
                    </div>
                    <div className="nk-timeline-content">
                      <div className="card">
                        <div className="card-inner">
                          <h6 className="title">{getActionLabel(event.description, event.event)}</h6>
                          <p className="text-soft small mb-2">
                            Par: {event.causer?.name || "Système"} | Événement:{" "}
                            <Badge color="info">{getEventLabel(event.event)}</Badge>
                          </p>
                          {event.properties && Object.keys(event.properties).length > 0 && (
                            <div className="mt-2">
                              <details>
                                <summary className="text-soft small cursor-pointer" style={{ cursor: "pointer" }}>
                                  Voir les détails
                                </summary>
                                <pre className="small bg-light p-2 rounded mt-2" style={{ maxHeight: "200px", overflow: "auto" }}>
                                  {JSON.stringify(event.properties, null, 2)}
                                </pre>
                              </details>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : loading ? (
              <div className="text-center p-4">
                <Spinner color="primary" />
              </div>
            ) : traceability ? (
              <div className="text-center p-4 text-soft">Aucun historique disponible</div>
            ) : null}
            {traceabilityTotalItems > 0 && (
              <div className="mt-3">
                <PaginationComponent
                  itemPerPage={traceabilityItemPerPage}
                  totalItems={traceabilityTotalItems}
                  paginate={paginateTraceability}
                  currentPage={traceabilityCurrentPage}
                />
              </div>
            )}
          </div>
        </PreviewCard>
      )}
    </div>
  );

  const renderAuditReports = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle page>Rapports d'audit</BlockTitle>
            <BlockDes className="text-soft">
              <p>Générer et consulter les rapports d'audit</p>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent>
            <Button color="primary" onClick={() => setReportModal(true)}>
              <Icon name="plus" className="me-1" />
              Générer un rapport
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      <PreviewCard className="card-full">
        <div className="card-inner">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : auditReports.length === 0 ? (
            <div className="text-center p-4 text-soft">Aucun rapport disponible</div>
          ) : (
            <DataTable className="card-stretch">
              <DataTableHead>
                <DataTableRow>
                  <DataTableItem>Titre</DataTableItem>
                  <DataTableItem>Type</DataTableItem>
                  <DataTableItem>Période</DataTableItem>
                  <DataTableItem>Créé par</DataTableItem>
                  <DataTableItem>Date</DataTableItem>
                  <DataTableItem>Statut</DataTableItem>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {auditReports.map((report) => (
                  <DataTableItem key={report.id}>
                    <DataTableRow>
                      <span className="fw-bold">{report.title}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <Badge color="info">{report.type}</Badge>
                    </DataTableRow>
                    <DataTableRow>
                      <span>
                        {new Date(report.period_start).toLocaleDateString("fr-FR")} -{" "}
                        {new Date(report.period_end).toLocaleDateString("fr-FR")}
                      </span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>{report.creator?.first_name} {report.creator?.last_name}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>{new Date(report.created_at).toLocaleDateString("fr-FR")}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <Badge color={report.status === "completed" ? "success" : "warning"}>
                        {report.status}
                      </Badge>
                    </DataTableRow>
                  </DataTableItem>
                ))}
              </DataTableBody>
            </DataTable>
          )}
        </div>
      </PreviewCard>
    </div>
  );

  const renderIsoCompliance = () => (
    <div>
      <BlockHead className="nk-block-head-sm">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle page>Conformité ISO</BlockTitle>
            <BlockDes className="text-soft">
              <p>Gestion de la conformité aux normes ISO</p>
            </BlockDes>
          </BlockHeadContent>
          <BlockHeadContent>
            <Button
              color="primary"
              onClick={() => {
                setEditingIso(null);
                setIsoModal(true);
              }}
            >
              <Icon name="plus" className="me-1" />
              Ajouter un enregistrement
            </Button>
          </BlockHeadContent>
        </BlockBetween>
      </BlockHead>

      {isoStats && (
        <Row className="g-gs mb-4">
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-primary">
                    {isoStats.compliance_rate || 0}%
                  </div>
                  <div className="text-soft">Taux de conformité</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-success">{isoStats.compliant || 0}</div>
                  <div className="text-soft">Conforme</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-danger">{isoStats.non_compliant || 0}</div>
                  <div className="text-soft">Non conforme</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
          <Col md="3">
            <PreviewCard className="card-full">
              <div className="card-inner">
                <div className="text-center">
                  <div className="display-4 fw-bold text-info">
                    {isoStats.total_requirements || 0}
                  </div>
                  <div className="text-soft">Total exigences</div>
                </div>
              </div>
            </PreviewCard>
          </Col>
        </Row>
      )}

      <PreviewCard className="card-full">
        <div className="card-inner">
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : isoRecords.length === 0 ? (
            <div className="text-center p-4 text-soft">Aucun enregistrement de conformité</div>
          ) : (
            <DataTable className="card-stretch">
              <DataTableHead>
                <DataTableRow>
                  <DataTableItem>Standard</DataTableItem>
                  <DataTableItem>Clause</DataTableItem>
                  <DataTableItem>Exigence</DataTableItem>
                  <DataTableItem>Statut</DataTableItem>
                  <DataTableItem>Date d'évaluation</DataTableItem>
                  <DataTableItem>Actions</DataTableItem>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {isoRecords.map((record) => (
                  <DataTableItem key={record.id}>
                    <DataTableRow>
                      <Badge color="primary">{record.standard}</Badge>
                    </DataTableRow>
                    <DataTableRow>
                      <span className="fw-bold">{record.clause}</span>
                    </DataTableRow>
                    <DataTableRow>
                      <span>{record.requirement}</span>
                    </DataTableRow>
                    <DataTableRow>{getStatusBadge(record.compliance_status)}</DataTableRow>
                    <DataTableRow>
                      <span>
                        {record.assessment_date
                          ? new Date(record.assessment_date).toLocaleDateString("fr-FR")
                          : "N/A"}
                      </span>
                    </DataTableRow>
                    <DataTableRow>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm"
                          color="primary"
                          onClick={() => {
                            setEditingIso(record);
                            setIsoForm({
                              standard: record.standard,
                              clause: record.clause,
                              requirement: record.requirement,
                              description: record.description,
                              compliance_status: record.compliance_status,
                              evidence: record.evidence || "",
                              notes: record.notes || "",
                              assessment_date: record.assessment_date || "",
                              next_review_date: record.next_review_date || "",
                            });
                            setIsoModal(true);
                          }}
                        >
                          <Icon name="edit" />
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          onClick={() => handleDeleteIso(record.id)}
                        >
                          <Icon name="trash" />
                        </Button>
                      </div>
                    </DataTableRow>
                  </DataTableItem>
                ))}
              </DataTableBody>
            </DataTable>
          )}
        </div>
      </PreviewCard>
    </div>
  );

  return (
    <React.Fragment>
      <Head title="Conformité & Audit" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Conformité & Audit</BlockTitle>
              <BlockDes>
                <p>
                  Audit trail, traçabilité, rapports d'audit et conformité ISO pour votre
                  organisation
                </p>
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Nav tabs className="nav-tabs-s2">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "audit-trail" })}
                onClick={() => {
                  setActiveTab("audit-trail");
                  history.push(`${process.env.PUBLIC_URL}/compliance/audit-trail`);
                }}
              >
                <Icon name="file-text"></Icon>
                <span>Audit Trail</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "traceability" })}
                onClick={() => {
                  setActiveTab("traceability");
                  history.push(`${process.env.PUBLIC_URL}/compliance/traceability`);
                }}
              >
                <Icon name="history"></Icon>
                <span>Traçabilité</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "audit-reports" })}
                onClick={() => {
                  setActiveTab("audit-reports");
                  history.push(`${process.env.PUBLIC_URL}/compliance/audit-reports`);
                }}
              >
                <Icon name="file-doc"></Icon>
                <span>Rapports d'audit</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "iso" })}
                onClick={() => {
                  setActiveTab("iso");
                  history.push(`${process.env.PUBLIC_URL}/compliance/iso`);
                }}
              >
                <Icon name="shield-check"></Icon>
                <span>Conformité ISO</span>
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="mt-4">
            <TabPane tabId="audit-trail">{renderAuditTrail()}</TabPane>
            <TabPane tabId="traceability">{renderTraceability()}</TabPane>
            <TabPane tabId="audit-reports">{renderAuditReports()}</TabPane>
            <TabPane tabId="iso">{renderIsoCompliance()}</TabPane>
          </TabContent>
        </Block>

        {/* Modal Génération de rapport */}
        <Modal isOpen={reportModal} toggle={() => setReportModal(false)} size="lg">
          <ModalHeader toggle={() => setReportModal(false)}>Générer un rapport d'audit</ModalHeader>
          <ModalBody>
            <Form>
              <Row className="g-3">
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Titre *</label>
                    <Input
                      type="text"
                      value={newReport.title}
                      onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                      placeholder="Titre du rapport"
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={newReport.description}
                      onChange={(e) =>
                        setNewReport({ ...newReport, description: e.target.value })
                      }
                      placeholder="Description du rapport"
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Date début *</label>
                    <Input
                      type="date"
                      value={newReport.period_start}
                      onChange={(e) =>
                        setNewReport({ ...newReport, period_start: e.target.value })
                      }
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Date fin *</label>
                    <Input
                      type="date"
                      value={newReport.period_end}
                      onChange={(e) =>
                        setNewReport({ ...newReport, period_end: e.target.value })
                      }
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="d-flex justify-content-end gap-2">
                    <Button color="secondary" onClick={() => setReportModal(false)}>
                      Annuler
                    </Button>
                    <Button color="primary" onClick={handleGenerateReport}>
                      Générer le rapport
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </ModalBody>
        </Modal>

        {/* Modal ISO Compliance */}
        <Modal isOpen={isoModal} toggle={() => setIsoModal(false)} size="lg">
          <ModalHeader toggle={() => setIsoModal(false)}>
            {editingIso ? "Modifier" : "Ajouter"} un enregistrement ISO
          </ModalHeader>
          <ModalBody>
            <Form>
              <Row className="g-3">
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Standard *</label>
                    <RSelect
                      options={[
                        { value: "ISO 45001", label: "ISO 45001" },
                        { value: "ISO 14001", label: "ISO 14001" },
                        { value: "ISO 9001", label: "ISO 9001" },
                      ]}
                      value={{ value: isoForm.standard, label: isoForm.standard }}
                      onChange={(option) => setIsoForm({ ...isoForm, standard: option.value })}
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Clause *</label>
                    <Input
                      type="text"
                      value={isoForm.clause}
                      onChange={(e) => setIsoForm({ ...isoForm, clause: e.target.value })}
                      placeholder="Ex: 8.1.2"
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Exigence *</label>
                    <Input
                      type="text"
                      value={isoForm.requirement}
                      onChange={(e) =>
                        setIsoForm({ ...isoForm, requirement: e.target.value })
                      }
                      placeholder="Titre de l'exigence"
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Description *</label>
                    <Input
                      type="textarea"
                      rows="3"
                      value={isoForm.description}
                      onChange={(e) => setIsoForm({ ...isoForm, description: e.target.value })}
                      placeholder="Description détaillée"
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Statut de conformité *</label>
                    <RSelect
                      options={[
                        { value: "compliant", label: "Conforme" },
                        { value: "non_compliant", label: "Non conforme" },
                        { value: "partially_compliant", label: "Partiellement conforme" },
                        { value: "not_applicable", label: "Non applicable" },
                      ]}
                      value={{
                        value: isoForm.compliance_status,
                        label:
                          isoForm.compliance_status === "compliant"
                            ? "Conforme"
                            : isoForm.compliance_status === "non_compliant"
                            ? "Non conforme"
                            : isoForm.compliance_status === "partially_compliant"
                            ? "Partiellement conforme"
                            : "Non applicable",
                      }}
                      onChange={(option) =>
                        setIsoForm({ ...isoForm, compliance_status: option.value })
                      }
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Date d'évaluation</label>
                    <Input
                      type="date"
                      value={isoForm.assessment_date}
                      onChange={(e) =>
                        setIsoForm({ ...isoForm, assessment_date: e.target.value })
                      }
                    />
                  </div>
                </Col>
                <Col md="6">
                  <div className="form-group">
                    <label className="form-label">Date de révision</label>
                    <Input
                      type="date"
                      value={isoForm.next_review_date}
                      onChange={(e) =>
                        setIsoForm({ ...isoForm, next_review_date: e.target.value })
                      }
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Preuve</label>
                    <Input
                      type="textarea"
                      rows="2"
                      value={isoForm.evidence}
                      onChange={(e) => setIsoForm({ ...isoForm, evidence: e.target.value })}
                      placeholder="Preuve de conformité"
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <Input
                      type="textarea"
                      rows="2"
                      value={isoForm.notes}
                      onChange={(e) => setIsoForm({ ...isoForm, notes: e.target.value })}
                      placeholder="Notes supplémentaires"
                    />
                  </div>
                </Col>
                <Col md="12">
                  <div className="d-flex justify-content-end gap-2">
                    <Button color="secondary" onClick={() => setIsoModal(false)}>
                      Annuler
                    </Button>
                    <Button color="primary" onClick={handleSaveIso}>
                      {editingIso ? "Modifier" : "Ajouter"}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default CompliancePage;
